import { useTranslation } from "react-i18next";

import { useToast } from "@/components/ui/status/Toast/useToast";
import { FileUtils } from "@/util/file.utils";

const useFileValidationCheck = () => {
  const { t } = useTranslation();
  const { errorToast } = useToast();

  const checkForValidNonSecurePdfFiles = async (
    files: File[],
    options?: { maxSizeMB: number },
  ): Promise<File[] | null> => {
    const validFiles = files.filter((file) => FileUtils.isValidFile(file, options?.maxSizeMB));
    let nonsecureFiles: File[] | null = [];
    if (validFiles.length) {
      // Check for encrypted/secure PDFs
      const securityChecks = await Promise.all(
        validFiles.map(async (file) => ({
          file,
          isSecure: await FileUtils.isSecurePDF(file),
        })),
      );

      const secureFiles = securityChecks.filter(({ isSecure }) => isSecure);
      nonsecureFiles = securityChecks.filter(({ isSecure }) => !isSecure).map(({ file }) => file);

      // Show error for secure files
      if (secureFiles.length > 0) {
        errorToast({
          text:
            secureFiles.length === 1
              ? t("uploads.files-upload-modal.secure-pdf-error")
              : t("uploads.files-upload-modal.secure-pdf-error-multiple"),
        });
      }
    }

    if (files?.length !== validFiles.length) {
      errorToast({
        text:
          files?.length === 1
            ? t("uploads.files-upload-modal.invalid-file-error", { maxSizeMB: options?.maxSizeMB })
            : t("uploads.files-upload-modal.invalid-file-error-multiple", { maxSizeMB: options?.maxSizeMB }),
      });
    }

    return nonsecureFiles;
  };

  return {
    checkForValidNonSecurePdfFiles,
  };
};

export default useFileValidationCheck;
